// #region Imports
import { Draft } from 'immer';
import { SheetManagementSet } from '../../types/sheet/draft';
import {
  Area,
  CellsReferences,
  NumberFormat,
  NumberFormats,
  NumberFormatType,
  RangeIndependencies,
  RangeIndepsMatrix,
  NumberFormatMatrix,
  Sheet,
} from '../../types';
import {
  AreaNumberFormat,
  CellNumberFormat,
} from '../../types/commands/sheetCommands/sheetCommands';
import {
  combineConsecutiveAreas,
  convertArrayAreaAddressToArea,
  createArrayAreaAddressFromArea,
  getIntersectedArea,
  isEqualAreas,
  reduceAreaByIntersection,
} from '../area/area';
import {
  addAddressToRangeMatrix,
  removeAddressFromRangeMatrix,
} from '../address/address';
import { IterateCellCBParam, iterateCells } from './iterateCells';
import {
  cellNumberFormatCommand
} from '../commands/generate/generateSheetCommands';
import { convertAreaToAddressString } from '../address/singleAddress';
// #endregion

// TODO: Calculate Dependent number formats For all Reference Types specially Data Validation

export async function applyNumberFormat(
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: CellNumberFormat
) {
  const { sheetId, formats } = command;
  const {
    numberFormatMatrix,
    numberFormats,
    cellsReferences,
    rangeIndepsMatrix,
    rangeIndeps,
  } = sheet;

  console.log(
    'formats',
    formats.map((format) => {
      return {
        address: convertAreaToAddressString(format.area, 100, 100),
        style: format.format,
      };
    })
  );

  // Saving Number Format
  const oldFormats: AreaNumberFormat[] = [];
  for (const format of formats) {
    startSaveNumberFormat(
      numberFormatMatrix,
      numberFormats,
      format,
      oldFormats
    );
  }

  const uniqueAreas = combineConsecutiveAreas(formats.map(({ area }) => area));

  console.log(
    'uniqueAreas',
    uniqueAreas.map((area) => convertAreaToAddressString(area, 100, 100))
  );

  // Clear Cell NF | Updating Dependent NF
  for (const area of uniqueAreas) {
    await updateDependentFormats(
      set,
      sheetId,
      cellsReferences,
      rangeIndepsMatrix,
      rangeIndeps,
      numberFormatMatrix,
      numberFormats,
      area,
      oldFormats
    );
  }

  console.log(
    'oldFormats',
    oldFormats.map((format) => {
      return {
        address: convertAreaToAddressString(format.area, 100, 100),
        style: format.format,
      };
    })
  );

  // Saving In Command For Undo / Redo
  cellNumberFormatCommand(set, sheetId, command, oldFormats);
}

const startSaveNumberFormat = (
  numberFormatMatrix: NumberFormatMatrix,
  numberFormats: NumberFormats,
  areaFormat: AreaNumberFormat,
  oldFormats: AreaNumberFormat[]
) => {
  const { area, format } = areaFormat;
  const address = createArrayAreaAddressFromArea(area);

  if (numberFormats[address]) {
    updateExistingRangeFormat(
      numberFormatMatrix,
      numberFormats,
      area,
      address,
      format,
      oldFormats
    );
  } else {
    saveFormatByResolvingIntersection(
      numberFormatMatrix,
      numberFormats,
      area,
      format,
      oldFormats
    );
  }
};

const saveFormatByResolvingIntersection = (
  numberFormatMatrix: string[][],
  numberFormats: NumberFormats,
  newArea: Area,
  newFormat: NumberFormat,
  oldFormats: AreaNumberFormat[]
) => {
  const existingFormats = Object.keys(numberFormats);
  for (const existingAddress of existingFormats) {
    const format = numberFormats[existingAddress];
    const existingArea = convertArrayAreaAddressToArea(existingAddress);
    if (!existingArea) continue;

    const intersection = getIntersectedArea(newArea, existingArea);
    if (intersection) {
      // Remove from current matrix
      removeAddressFromRangeMatrix(
        numberFormatMatrix,
        existingArea,
        existingAddress
      );
      reduceAreaByIntersection(existingArea, intersection).forEach((area) => {
        startSaveNonIntersectedFormat(
          numberFormatMatrix,
          numberFormats,
          area,
          format
        );
      });
      saveFormatInOldFormats(oldFormats, intersection, format);
      delete numberFormats[existingAddress];
    }
  }

  startSaveNonIntersectedFormat(
    numberFormatMatrix,
    numberFormats,
    newArea,
    newFormat
  );

  const resetDefaultFormat = {
    type: NumberFormatType.Automatic,
    code: '',
  };

  // On Undo Apply default format first and then the old saved formats
  saveFormatInOldFormats(oldFormats, newArea, resetDefaultFormat, false);

  return oldFormats;
};

function startSaveNonIntersectedFormat(
  numberFormatMatrix: NumberFormatMatrix,
  numberFormats: NumberFormats,
  area: Area,
  format: NumberFormat
) {
  if (format.type !== NumberFormatType.Automatic) {
    const arrayAreaAddress = createArrayAreaAddressFromArea(area);
    // Add to current matrix
    addAddressToRangeMatrix(numberFormatMatrix, area, arrayAreaAddress);
    numberFormats[arrayAreaAddress] = { ...format };
  }
}

const updateExistingRangeFormat = (
  numberFormatMatrix: NumberFormatMatrix,
  numberFormats: NumberFormats,
  area: Area,
  address: string,
  format: NumberFormat,
  oldFormats: AreaNumberFormat[]
) => {
  saveFormatInOldFormats(oldFormats, area, numberFormats[address]);
  if (format.type === NumberFormatType.Automatic) {
    delete numberFormats[address];
    removeAddressFromRangeMatrix(numberFormatMatrix, area, address);
  } else {
    numberFormats[address] = format;
  }
};

async function updateDependentFormats(
  set: SheetManagementSet,
  sheetId: string,
  cellsReferences: CellsReferences,
  rangeIndepsMatrix: RangeIndepsMatrix,
  rangeIndeps: RangeIndependencies,
  numberFormatMatrix: NumberFormatMatrix,
  numberFormats: NumberFormats,
  area: Area,
  oldFormats: AreaNumberFormat[]
) {
  console.log(convertAreaToAddressString(area, 100, 100));
  // const isSingle = isSingleCell(area);
  const cb = async ({
    position,
    cell: cellRef,
  }: IterateCellCBParam<CellsReferences>) => {
    const indeps = cellRef?.i;
  };
  await iterateCells(cellsReferences, area, cb);

  //   TODO: Iterate over range Calculate independents
}

const saveFormatInOldFormats = (
  oldFormats: AreaNumberFormat[],
  area: Area,
  format: NumberFormat,
  isPush = true
) => {
  const index = oldFormats.findIndex((oldFormat) =>
    isEqualAreas(area, oldFormat.area)
  );
  if (index === -1) {
    if (isPush)
      oldFormats.push({
        area: area,
        format: { ...format },
      });
    else {
      oldFormats.unshift({
        area,
        format: { ...format },
      });
    }
  }
};

// async function updateDependentFormats(
//   set: SheetManagementSet,
//   sheetId: string,
//   cellsReferences: CellsReferences,
//   rangeIndeps: RangeIndependencies,
//   numberFormatMatrix: NumberFormatMatrix,
//   numberFormats: NumberFormats,
//   area: Area
// ) {
//   // Iterate over cells
//   // Clear Number Formats
//   const cb = async ({
//     position,
//     cell: cellRef,
//   }: IterateCellCBParam<CellsReferences>) => {
//     if (cellRef) {
// TODO: Save existing format in oldFormats
// TODO: If area single cell do not clear number format
//       deleteCellNumberFormat(cellsReferences, cellRef, position);
//       // TODO: Update number format of indeps
//       // if (cellRef.i) {
//       //   for (let { sid: depSheetId, addr, type } of cellRef.i) {
//       //     let dependency: ComputeIndepsDependency | undefined;
//       //     if (type === ReferenceType.CellFormula) {
//       //       const isCell = isCellPosition(addr);
//       //       if (!isCell) continue;
//       //       dependency = {
//       //         type: ReferenceType.CellFormula,
//       //         cellAddress: addr,
//       //         calculateSelf: true,
//       //         cellPosition: isCell.position,
//       //       };
//       //     } else {
//       //       dependency = {
//       //         type,
//       //         id: addr,
//       //       };
//       //     }
//       //     if (dependency)
//       //       await startComputingIndeps(set, depSheetId, dependency, true);
//       //   }
//       // }
//     }
//   };
//   await iterateCells(cellsReferences, area, cb);

//   // get cell Indeps and calculate independent number formats
//   // get Range Indeps and calcualte independent number formats
//   const visitedAddressMap: Map<string, boolean> = new Map();
//   for (let c = 0; c <= area.end.ci; c++) {
//     const colMatrix = rangeIndeps[c];
//     if (colMatrix) {
//       for (const { sid, addr, type } of colMatrix) {
//         if (visitedAddressMap.has(addr)) continue;

//         visitedAddressMap.set(addr, true);
//       }
//     }
//   }
// }
